import { useEffect, useState } from "react"
import axios from "axios"
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline"
import Modal from "@/components/Modal"
import Button from "@/components/Button"

type FideResult = {
  fide_id: string
  name: string
  title: string | null
  federation: string
  std_rating: string | null
  rpd_rating: string | null
  blz_rating: string | null
  birth_year: string | null
}

const FideLookupModal = ({
  show,
  onClose,
  onSelect,
  initialQuery,
}: {
  show: boolean
  onClose: () => void
  onSelect: (fideId: string) => void
  initialQuery?: string
}) => {
  const [query, setQuery] = useState(initialQuery ?? "")
  const [results, setResults] = useState<FideResult[]>([])
  const [searching, setSearching] = useState(false)
  const [searched, setSearched] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset state when modal opens
  useEffect(() => {
    if (show) {
      setQuery(initialQuery ?? "")
      setResults([])
      setSearched(false)
      setError(null)
    }
  }, [show, initialQuery])

  const handleSearch = async (e?: React.FormEvent) => {
    e?.preventDefault()
    if (query.trim().length < 2) return

    try {
      setSearching(true)
      setSearched(false)
      setError(null)
      const response = await axios.get<FideResult[]>("/api/fide/search", {
        params: { q: query.trim() },
      })
      setResults(response.data)
      setSearched(true)
    } catch {
      setError("Could not search FIDE. Please try again.")
      setResults([])
      setSearched(false)
    } finally {
      setSearching(false)
    }
  }

  const handleSelect = (fideId: string) => {
    onSelect(fideId)
    onClose()
  }

  return (
    <Modal
      show={show}
      onClose={onClose}
      title="Look up FIDE ID"
      maxWidthClassName="sm:max-w-xl"
    >
      <form onSubmit={handleSearch} className="mt-4">
        <div className="flex gap-x-2">
          <div className="relative flex-1">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <MagnifyingGlassIcon className="size-4 text-gray-400" />
            </div>
            <input
              type="text"
              autoFocus
              placeholder="Search by name (e.g. Henderson)"
              className="focus:ring-chessdrop-600 block w-full rounded-lg border-0 py-2 pl-9 pr-3 text-sm text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <Button type="submit" size="sm" loading={searching}>
            Search
          </Button>
        </div>
      </form>

      <div className="mt-4 max-h-72 overflow-y-auto">
        {results.length > 0 ? (
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 bg-gray-50 text-xs text-gray-500">
              <tr>
                <th className="px-2 py-1.5 font-medium">Name</th>
                <th className="px-2 py-1.5 font-medium">Title</th>
                <th className="px-2 py-1.5 font-medium">Fed</th>
                <th className="px-2 py-1.5 font-medium text-right">Std</th>
                <th className="px-2 py-1.5 font-medium" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {results.map((result) => (
                <tr key={result.fide_id} className="hover:bg-gray-50">
                  <td className="px-2 py-2 font-medium text-gray-900">
                    {result.name}
                  </td>
                  <td className="px-2 py-2 text-gray-500">
                    {result.title ?? "—"}
                  </td>
                  <td className="px-2 py-2 text-gray-500">
                    {result.federation}
                  </td>
                  <td className="px-2 py-2 text-right text-gray-500">
                    {result.std_rating ?? "—"}
                  </td>
                  <td className="px-2 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => handleSelect(result.fide_id)}
                      className="text-chessdrop-600 hover:text-chessdrop-700 text-xs font-medium hover:underline"
                    >
                      Select
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : searched ? (
          <p className="py-6 text-center text-sm text-gray-500">
            No results found. Try a different spelling or last name.
          </p>
        ) : error ? (
          <p className="py-6 text-center text-sm text-red-500">{error}</p>
        ) : null}
      </div>
    </Modal>
  )
}

export default FideLookupModal
